import { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import SEO from '@seo/index';
import RankPredictor from '@cont/RankPredictor/RankPredictor';
import {
  getFaqs,
  getMetaInfo,
  staticPagePathParams
} from '@cont/RankPredictor/utils';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { genarateSchema } from '@seo/rankpredictor/rankpredictor';
import Layout from '@layout/FreeLayout/FreeLayout';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import { RANK_PREDICTOR } from '@constants/segment';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';

const RANK_PREDICTOR_GOAL_SLUG_MAPPING = {
  YOTUH: 'neet-rank-predictor'
};

const RankPredictorLanding = ({ pageMetaInfo }) => {
  const { query, asPath } = useRouter();
  const { goalUID } = query;

  const { title, description } = getMetaInfo(goalUID);
  const faqs = getFaqs(goalUID);
  const metaTitle = title || pageMetaInfo?.title;
  const metaDescription = description || pageMetaInfo?.description;

  const url = `https://unacademy.com${asPath}`;

  const { closeElement: closeLoginDrawer } = useToggleable(LOGIN_DRAWER);

  useEffect(() => {
    closeLoginDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          metaTitle,
          metaDescription
        }}
        url={url}
        schema={genarateSchema({
          faqs,
          predictorURL: url
        })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <RankPredictor />
    </>
  );
};

RankPredictorLanding.pageInfo = {
  isMWebSupported: true,
  name: RANK_PREDICTOR,
  layoutOptions: {
    hideHeader: true,
    hideIntercom: true,
    hideFreshbot: true
  }
};

export const getStaticProps = async ({ params }) => {
  const { goalUID, goalSlug } = params;
  if (
    Object.keys(RANK_PREDICTOR_GOAL_SLUG_MAPPING).includes(goalUID) &&
    goalSlug !== RANK_PREDICTOR_GOAL_SLUG_MAPPING[goalUID]
  ) {
    return {
      redirect: {
        destination: `/rankpredictor/${RANK_PREDICTOR_GOAL_SLUG_MAPPING[goalUID]}/${goalUID}`,
        statusCode: 301
      }
    };
  }
  try {
    const [goal, metaInfo] = await Promise.all([
      fetchGoalInfo(goalUID),
      fetchPageMetaInfo(RANK_PREDICTOR, goalUID)
    ]);
    if (goal.errorCode) {
      return { notFound: true, revalidate: 60 };
    }
    return {
      props: {
        pageMetaInfo: metaInfo || null,
        globalFallbackData: {
          [getTopologyKey(goal.uid)]: goal
        }
      },
      revalidate: 60 * 5
    };
  } catch (error) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  const paths = staticPagePathParams.map((params) => ({ params }));
  return {
    paths,
    fallback: 'blocking'
  };
};

export default RankPredictorLanding;
RankPredictorLanding.Layout = Layout;
RankPredictorLanding.Wrapper = null;
